import React, { type ReactNode } from 'react';
import { Capability, PlanIds } from '../data/account';
import { WrappedButton } from '../ui/button/WrappedButton.component';
import { WrappedButtonType } from '../ui/button/WrappedButtonType.enum';
import BillingPlans from '../utils/BillingPlans';
import Navigation from '../utils/Navigation';
import Session from '../utils/Session';
import UpgradePlanModal from './UpgradePlanModal.component';

export const shouldShowUpgradeIcon = (): boolean => {
	// Gate on the effective (active-or-pending) plan: a trial user who has
	// already scheduled an upgrade to Professional403+ shouldn't see a prompt
	// urging them to upgrade to Professional.
	const currentPlan = BillingPlans.getCurrentPlan();
	const activePlan = BillingPlans.getCurrentActivePlan();
	// New trial accounts come with pendingPlanId=Basic402 by default from the
	// backend — treat that pairing as "still on Trial"
	const effectivePlanId =
		currentPlan?.PlanId === PlanIds.Trial401 &&
		activePlan?.PlanId === PlanIds.Basic402
			? currentPlan.PlanId
			: activePlan?.PlanId;
	return BillingPlans.isNewLineUpTrialPlan(effectivePlanId);
};

type ShowModal = (config: { component: any; data: any }) => void;

/**
 * Opens the Professional upgrade modal. Shared entry point so every gated-
 * feature surface (left menu row click, toolbar, trial popover) lands on the
 * same modal content and plan details.
 */
export const showUpgradePlanModal = (
	showModal: ShowModal,
	opts: { triggerFeature?: string } = {}
): void => {
	// Professional users upgrade to MultiLocation; everyone else upgrades to Professional.
	const currentPlanId = BillingPlans.getCurrentPlan()?.PlanId;
	const targetPlanId =
		currentPlanId === PlanIds.Professional403
			? PlanIds.MultiLocation404
			: PlanIds.Professional403;

	// Pull full plan record (incl. backend Summary) from BillingPlans cache,
	// fall back to a synthetic shell only if the endpoint hasn't resolved yet.
	const plan =
		BillingPlans.getPlanByPlanId(targetPlanId) ??
		(targetPlanId === PlanIds.MultiLocation404
			? {
					Name: 'MultiLocation',
					Price: 69.99,
					PlanId: PlanIds.MultiLocation404,
					Sku: 1020000 + PlanIds.MultiLocation404,
					MaxAllowedUsers: 5,
				}
			: {
					Name: 'Professional',
					Price: 29.99,
					PlanId: PlanIds.Professional403,
					Sku: 1020000 + PlanIds.Professional403,
					MaxAllowedUsers: 3,
				});

	showModal({
		component: UpgradePlanModal,
		data: {
			upgradePlan: plan,
			triggerFeature: opts.triggerFeature,
		},
	});
};

/**
 * True when the current user is on new lineup (non-trial) and does NOT have
 * the cost_codes AppCap. In that state, cost-code surfaces render a plain
 * upgrade icon and clicking them opens the upgrade modal.
 */
export const isCostCodesGated = (): boolean =>
	!BillingPlans.isNewLineUpTrialPlan() &&
	BillingPlans.isNewLineupPlan() &&
	!Session.hasCapability(Capability.CostCodes as any);

const FEATURE_CONTENT = {
	costCodes: {
		softGate: {
			title: 'Take This Professional Feature for a Spin',
			paragraphs: [
				'Cost codes allow you to track costs by customer, department, employee, or any other areas of your business.',
			],
		},
		hardGate: {
			title: 'Unlock This Professional Feature',
			paragraphs: [
				'Cost codes allow you to track costs by customer, department, employee, or any other areas of your business. Upgrade to the Professional plan to unlock this feature!',
			],
		},
	},
	referenceNumber: {
		softGate: {
			title: 'Take This Professional Feature for a Spin',
			paragraphs: [
				'Reference codes allows you to add a custom message that will print on your shipping labels. This is a great way to add a personal touch to your label or add information to the label that is required in your shipping workflow.',
			],
		},
		hardGate: {
			title: 'Unlock This Professional Feature',
			paragraphs: [
				'Reference codes allows you to add a custom message that will print on your shipping labels. This is a great way to add a personal touch to your label or add information to the label that is required in your shipping workflow. Upgrade to the Professional plan to unlock this feature!',
			],
		},
	},
};

export type UpgradeFeatureType = keyof typeof FEATURE_CONTENT;

const isSoftGate = (): boolean => {
	// Soft gate: user is on trial (can test Professional features)
	// Hard gate: user is on Basic without the capability (feature is blocked)
	return BillingPlans.isNewLineUpTrialPlan();
};

const renderContent = (featureType: UpgradeFeatureType) => {
	const gateType = isSoftGate() ? 'softGate' : 'hardGate';
	const content = FEATURE_CONTENT[featureType][gateType];
	return {
		title: content.title,
		message: (
			<>
				{content.paragraphs.map((text: string, i: number) => (
					<p key={i}>{text}</p>
				))}
			</>
		),
	};
};

const renderActions =
	(featureType: UpgradeFeatureType, showModal: ShowModal) =>
	(closePopover: () => void): ReactNode[] => {
		const gateType = isSoftGate() ? 'softGate' : 'hardGate';

		const handleUpgradeNow = () => {
			closePopover();
			showUpgradePlanModal(showModal, {
				triggerFeature: FEATURE_CONTENT[featureType][gateType].title,
			});
		};

		const handleComparePlans = () => {
			closePopover();
			Navigation.navigateTo('storeChangePlan');
		};

		// Soft gate: only show "Compare Plans" button
		if (gateType === 'softGate') {
			return [
				<WrappedButton
					key="compare-plans"
					buttonType={WrappedButtonType.TertiaryGhost}
					label="Compare Plans"
					dataqa="trial-upgrade-popover-compare"
					onClick={handleComparePlans}
				/>,
			];
		}

		// Hard gate: show both "Compare Plans" and "Upgrade Now" buttons
		return [
			<WrappedButton
				key="compare-plans"
				buttonType={WrappedButtonType.TertiaryGhost}
				label="Compare Plans"
				dataqa="upgrade-popover-compare"
				onClick={handleComparePlans}
			/>,
			<WrappedButton
				key="upgrade-now"
				buttonType={WrappedButtonType.Primary}
				label="Upgrade Now"
				dataqa="upgrade-popover-upgrade-now"
				onClick={handleUpgradeNow}
			/>,
		];
	};

/**
 * Single helper for call sites. Spreads onto any button/menu item that supports
 * the upgrade-icon props. Returns empty object when the user is not on a trial
 * or basic plan, so the icon just doesn't render.
 */
export const getUpgradeIconProps = (
	featureType: UpgradeFeatureType,
	{ showModal }: { showModal: ShowModal }
) => {
	if (!shouldShowUpgradeIcon()) return {};
	return {
		showUpgradeIcon: true,
		upgradePopoverContent: renderContent(featureType),
		upgradePopoverActions: renderActions(featureType, showModal),
	};
};
